import { useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react'
import { motion } from '@/utils/motion'
import { RatingButtons } from './RatingButtons'

interface RatingPickerProps {
  /** Where the picked point sits, as a fraction of the chart's width and height. */
  at: { x: number; y: number }
  title: string
  review: number
  selected: number
  onRate: (rating: number) => void
  onClose: () => void
}

/** A small card beside a timeline point, asking which rating that review gets. */
export function RatingPicker({ at, title, review, selected, onRate, onClose }: RatingPickerProps) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!ref.current) return
    ref.current.querySelector('button')?.focus()
    void motion.rise(ref.current)
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [onClose])

  // A phone has no room beside the point, so the card spans the chart there; wider
  // screens open it towards the roomier side so it never hangs off the chart.
  const style = {
    top: `${at.y * 100}%`,
    ...(at.x > 0.5 ? { '--pick-right': `${(1 - at.x) * 100}%` } : { '--pick-left': `${at.x * 100}%` }),
  } as CSSProperties

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={title}
      style={style}
      className="absolute inset-x-0 z-20 mt-4 rounded-md sm:inset-x-auto sm:left-(--pick-left) sm:right-(--pick-right) sm:w-96 border border-border-divider bg-card p-4 shadow-lg"
    >
      <p className="font-serif text-sm text-heading">{title}</p>
      <p className="mb-3 font-serif text-sm text-muted">Choose how this review goes.</p>
      <RatingButtons label={`Rate review ${review}`} selected={selected} onRate={onRate} />
    </div>
  )
}
